import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useStyles } from './FlyOutArnDetails.styles'
import { CFSButtonV2, TextInputV4 } from '@labrav/react-components'
import type { TestConnectionStatusProps } from '../../../../modules/flyOutOnboarding/types'
import {
  flyOutCFTLink,
  flyOutCFTLinkNotOwnS3Bucket,
  flyOutCFTUrl,
  flyoutFormDebounceTime,
  qaFlyoutCFTLink,
  qaFlyOutCFTLinkNotOwnS3Bucket,
  isQaServiceEnvironment,
} from '../../../../common/utils/constants'
import { FormikProps, useFormikContext } from 'formik'
import { useAccountId } from '@labrav/common'
import { flyOutTestConnection } from '../../../../modules/flyOutOnboarding/action'
import { useDispatch } from '../../../../store'

import { Typography } from '@mui/material'
import { clsx } from 'clsx'
import type {
  FlyOutCloudSettingsFormValues,
  SellerMetadataFieldErrors,
  SellerMetadataTouched,
} from '../../types'
import { ShowConnectionStatuses } from '../../../FlyOutCloudSettings/FlyOutArnDetails/FlyOutArnDetails'
import { useFlagValue } from '@labrav/flags'

export type FlyOutArnDetailsProps = {
  setConnectionTested: React.Dispatch<React.SetStateAction<boolean>>
  accountId?: string
  showConnectionStatus?: boolean
}

export const FlyOutArnDetails: React.FC<FlyOutArnDetailsProps> = ({
  setConnectionTested,
  accountId: accountId,
  showConnectionStatus = true,
}) => {
  const {
    values,
    errors,
    touched,
    status,
    setStatus,
    handleBlur,
    handleChange,
    setFieldTouched,
    setFieldValue,
  } = useFormikContext() as FormikProps<FlyOutCloudSettingsFormValues>
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const partnerIdFromHook = useAccountId({ product: 'flyout' })
  const { value: isMarketplaceCloudSettingsRevampEnabled } = useFlagValue(
    'marketplaceCloudSettingsRevamp'
  )
  const { value: isOnboardingImprovementsEnabled } = useFlagValue(
    'onboardingImprovements'
  )

  const partnerId = accountId || partnerIdFromHook
  const isOwnS3Bucket = values.isOwnS3Bucket === true
  const stackType = values.stackType
  const isTerraformStack =
    Boolean(isOnboardingImprovementsEnabled) && stackType === 'terraform'
  const failedConnectionField = (
    status as { failedConnectionField?: 'awsRoleArn' | 'awsCasS3BucketName' }
  )?.failedConnectionField

  const [testConnectionStatus, setTestConnectionStatus] =
    useState<TestConnectionStatusProps>({
      status: 'standBy',
    })
  const testConnectionDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const { awsRoleArn } = values.sellerMetadata
  const sellerMetadataErrors = errors.sellerMetadata as
    | SellerMetadataFieldErrors
    | undefined
  const sellerMetadataTouched = touched.sellerMetadata as
    | SellerMetadataTouched
    | undefined

  useEffect(() => {
    setConnectionTested(testConnectionStatus.status === 'success')
  }, [testConnectionStatus])

  const testConnection = async () => {
    if (isMarketplaceCloudSettingsRevampEnabled) {
      if (!isOwnS3Bucket) {
        setTestConnectionStatus({ status: 'standBy' })
        return
      }
    }
    if (awsRoleArn) {
      await dispatch(
        flyOutTestConnection(
          partnerId || '',
          awsRoleArn,
          isMarketplaceCloudSettingsRevampEnabled,
          setTestConnectionStatus
        )
      )
    }
  }

  useEffect(() => {
    testConnection()
  }, [])

  const handleChangeField = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    await handleChange(event)
    if (event?.target?.name === 'sellerMetadata.awsRoleArn') {
      if (isTerraformStack) {
        void setFieldValue('terraformConnectionValidated', false)
      }
      setTestConnectionStatus({ status: 'standBy' })
      setStatus?.({
        ...(status as Record<string, unknown>),
        arnConnectionStatus: 'standBy',
      })
      if (testConnectionDebounceRef.current) {
        clearTimeout(testConnectionDebounceRef.current)
      }
      const arn = event.target.value || ''
      if (arn) {
        void setFieldTouched('sellerMetadata.awsRoleArn', true, true)
      }
      if (isMarketplaceCloudSettingsRevampEnabled) {
        if (!arn || !isOwnS3Bucket) {
          return
        }
      }
      if (!arn) {
        return
      }
      testConnectionDebounceRef.current = setTimeout(() => {
        void dispatch(
          flyOutTestConnection(
            partnerId || '',
            arn,
            isMarketplaceCloudSettingsRevampEnabled,
            setTestConnectionStatus
          )
        )
      }, flyoutFormDebounceTime)
    }
  }

  const handleBlurRoleArn = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    await handleBlur(event)
  }

  useEffect(() => {
    return () => {
      if (testConnectionDebounceRef.current) {
        clearTimeout(testConnectionDebounceRef.current)
      }
    }
  }, [])

  useEffect(() => {
    setStatus?.({
      ...(status as Record<string, unknown>),
      arnConnectionStatus: testConnectionStatus.status,
    })
  }, [testConnectionStatus.status])

  const cftLaunchLink = useMemo(() => {
    const base = isOwnS3Bucket ? flyOutCFTLink : flyOutCFTLinkNotOwnS3Bucket
    const externalId = String(partnerId ?? '')
    const partnerOrgName = String(values.awsAccountName ?? '')
    const modifiedPartnerOrgname = partnerOrgName
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
    const link = `${base}&param_ExternalId=${externalId}&param_PartnerOrgName=${modifiedPartnerOrgname}`
    return link
  }, [isOwnS3Bucket, values.awsAccountName, partnerId])

  const arnValidationMessage =
    sellerMetadataTouched?.awsRoleArn && sellerMetadataErrors?.awsRoleArn
      ? String(sellerMetadataErrors.awsRoleArn)
      : ''

  const isRoleArnFilled = Boolean(String(awsRoleArn ?? ''))

  let cftLink
  if (isMarketplaceCloudSettingsRevampEnabled) {
    cftLink = cftLaunchLink
  } else {
    cftLink = flyOutCFTLink
  }

  return (
    <div className={classes.fieldsArnContainer}>
      {isTerraformStack ? '' : <CFSButtonV2 cfLink={cftLink} />}
      <div className={classes.arnInputAndStatus}>
        <div className={classes.fieldsContainer}>
          <div className={clsx(classes.field)}>
            <TextInputV4
              keyName="sellerMetadata.awsRoleArn"
              title="Enter your platform role ARN"
              type="text"
              placeHolder=""
              value={awsRoleArn || ''}
              isError={
                !!(
                  sellerMetadataTouched?.awsRoleArn &&
                  sellerMetadataErrors?.awsRoleArn
                ) ||
                (isOwnS3Bucket
                  ? testConnectionStatus.status === 'failure'
                  : failedConnectionField === 'awsRoleArn')
              }
              onChange={handleChangeField}
              onBlur={handleBlurRoleArn}
              isReadOnly={false}
              isRequired={false}
              errorText=""
              showSpinner={true}
            />
            {arnValidationMessage ? (
              <div className={classes.arnHelperRow}>
                <Typography className={classes.arnValidationErrorText}>
                  {arnValidationMessage}
                </Typography>
              </div>
            ) : !isRoleArnFilled ? (
              <div className={classes.arnHelperRow}>
                <Typography className={classes.arnHintText}>
                  E.g. arn:aws:iam::023678306288:role/flyout-role-labra
                </Typography>
              </div>
            ) : null}
          </div>
        </div>
        {showConnectionStatus ? (
          <ShowConnectionStatuses testConnectionStatus={testConnectionStatus} />
        ) : null}
      </div>
    </div>
  )
}
